import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Alert } from '../../../common/Alert';
import { useFileUpload, UploadProgress } from '../../../../api/file-upload';
import {
  validateFile,
  processFileWithUpload,
  FileUploadProcessingResult,
} from '../../../../utils/fileUtils';
import { ACCEPT_TYPES } from '../../../../constants';
import type { AttachedFile } from './types';
import { useChatStore } from '../../store/chatStore';

import { useTranslation } from 'react-i18next';

interface FileAttachmentProps {
  onFilesUploaded: (results: FileUploadProcessingResult[]) => void;
  onUploadProgress?: (
    files: Array<{ file: File; progress: UploadProgress }>,
  ) => void;
  disabled?: boolean;
  className?: string;
  showProgress?: boolean;
  // Global constraints
  maxTotalFiles?: number; // default 20
  maxTotalSize?: number; // bytes, default 20MB
  // Parent state so we can compute totals pragmatically
  attachedFiles?: AttachedFile[];
  uploadedFiles?: FileUploadProcessingResult[];
  /** When set, uploads go into the conversation workspace so the agent can access them. */
  conversationId?: string;
}

interface FileUploadState {
  file: File;
  progress: UploadProgress | null;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
  result?: FileUploadProcessingResult;
}

export const FileAttachment: React.FC<FileAttachmentProps> = ({
  onFilesUploaded,
  onUploadProgress,
  disabled = false,
  className = '',
  showProgress = true,
  maxTotalFiles = 20,
  maxTotalSize = 20 * 1024 * 1024,
  attachedFiles = [],
  uploadedFiles = [],
  conversationId,
}) => {
  const { t } = useTranslation('pleri/chat');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef<number>(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const [_, setErrorMessages] = useState<string[]>([]);
  const [lastError, setLastError] = useState<string | null>(null);

  // Auto-dismiss toast error after a short delay
  useEffect(() => {
    if (!lastError) {
      return;
    }
    const timeoutId = window.setTimeout(() => setLastError(null), 5000);
    return () => window.clearTimeout(timeoutId);
  }, [lastError]);
  const [uploadStates, setUploadStates] = useState<
    Map<string, FileUploadState>
  >(new Map());
  const { uploadFile } = useFileUpload();
  const incrementActiveUploads = useChatStore((s) => s.incrementActiveUploads);
  const decrementActiveUploads = useChatStore((s) => s.decrementActiveUploads);
  const focusChatInput = useChatStore((s) => s.focusChatInput);

  const handleFileSelect = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) {
        return;
      }

      let fileArray = Array.from(files);

      // Compute totals from parent state
      const currentTotalFiles = attachedFiles.length + uploadedFiles.length;
      const currentTotalSize =
        attachedFiles.reduce((sum, f) => sum + (f.file?.size || 0), 0) +
        uploadedFiles.reduce(
          (sum, r) => sum + (r.uploadResult?.fileSize || 0),
          0,
        );

      // Compute remaining allowances
      const remainingFiles = Math.max(0, maxTotalFiles - currentTotalFiles);
      const remainingSize = Math.max(0, maxTotalSize - currentTotalSize);

      if (remainingFiles === 0 || remainingSize === 0) {
        const msg = `Attachment limit reached. You can attach up to ${maxTotalFiles} files or ${Math.floor(maxTotalSize / (1024 * 1024))}MB total.`;
        setErrorMessages((prev) => {
          const merged = [...prev, msg];
          return merged.slice(-5);
        });
        setLastError(msg);
        return;
      }

      // Apply remaining file count cap
      if (fileArray.length > remainingFiles) {
        const skipped = fileArray.length - remainingFiles;
        fileArray = fileArray.slice(0, remainingFiles);
        const msg = `You can attach at most ${maxTotalFiles} files in total. Skipped ${skipped} file(s).`;
        setErrorMessages((prev) => {
          const merged = [...prev, msg];
          return merged.slice(-5);
        });
        setLastError(msg);
      }

      // Reject files with the same name as already attached or uploaded (and duplicates within selection)
      const existingNames = new Set<string>();
      for (const f of attachedFiles) {
        if (f.file?.name) {
          existingNames.add(f.file.name);
        }
      }
      for (const r of uploadedFiles) {
        const name = r.uploadResult?.fileName;
        if (typeof name === 'string') {
          existingNames.add(name);
        }
      }
      const duplicateSkipped: string[] = [];
      fileArray = fileArray.filter((file) => {
        if (existingNames.has(file.name)) {
          duplicateSkipped.push(file.name);
          return false;
        }
        existingNames.add(file.name);
        return true;
      });
      if (duplicateSkipped.length > 0) {
        const msg =
          duplicateSkipped.length === 1
            ? `A file with the same name is already attached: ${duplicateSkipped[0]}`
            : t('theFollowingFilesWereSkippedBecauseA', {
                duplicateSkipped: duplicateSkipped.join(', '),
              });
        setErrorMessages((prev) => {
          const merged = [...prev, msg];
          return merged.slice(-5);
        });
        setLastError(msg);
      }
      if (fileArray.length === 0) {
        return;
      }

      // Apply remaining total size cap across existing + new
      let usedSize = 0;
      const withinTotalLimit: File[] = [];
      for (const f of fileArray) {
        if (usedSize + f.size <= remainingSize) {
          withinTotalLimit.push(f);
          usedSize += f.size;
        } else {
          break; // stop when next file would exceed the remaining budget
        }
      }
      if (withinTotalLimit.length < fileArray.length) {
        const skipped = fileArray.length - withinTotalLimit.length;
        const msg = `Total attachment size limit is ${Math.floor(maxTotalSize / (1024 * 1024))}MB. Skipped ${skipped} file(s).`;
        setErrorMessages((prev) => {
          const merged = [...prev, msg];
          return merged.slice(-5);
        });
        setLastError(msg);
      }
      fileArray = withinTotalLimit;

      const validFiles: File[] = [];
      const validationErrors: string[] = [];

      // Validate files first
      fileArray.forEach((file) => {
        const validation = validateFile(file);
        if (validation.valid) {
          validFiles.push(file);
        } else {
          validationErrors.push(`${file.name}: ${validation.error}`);
        }
      });

      if (validationErrors.length > 0) {
        setErrorMessages((prev) => {
          const merged = [...prev, ...validationErrors];
          return merged.slice(-5);
        });
        setLastError(validationErrors[validationErrors.length - 1] || null);
      }

      if (validFiles.length === 0) {
        return;
      }

      // Initialize upload states and create temporary attached files for preview
      const newUploadStates = new Map(uploadStates);
      const tempAttachedFiles: { file: File; preview?: string }[] = [];

      for (const file of validFiles) {
        const fileId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        newUploadStates.set(fileId, {
          file,
          progress: null,
          status: 'pending',
        });

        // Create preview for images to show immediately
        if (file.type.startsWith('image/')) {
          const preview = URL.createObjectURL(file);
          tempAttachedFiles.push({ file, preview });
        } else {
          tempAttachedFiles.push({ file });
        }
      }
      setUploadStates(newUploadStates);

      // Process uploads in parallel
      const results: FileUploadProcessingResult[] = [];
      const uploads = Array.from(newUploadStates.entries()).map(
        ([fileId, state]) => {
          if (state.status !== 'pending') {
            return Promise.resolve();
          }

          // Mark as uploading before starting
          setUploadStates((prev) => {
            const updated = new Map(prev);
            const existing = updated.get(fileId) || state;
            updated.set(fileId, { ...existing, status: 'uploading' });
            return updated;
          });
          incrementActiveUploads(1);

          return processFileWithUpload(
            state.file,
            (file, onProgress) =>
              uploadFile(file, onProgress, { conversationId }),
            (progress) => {
              setUploadStates((prev) => {
                const updated = new Map(prev);
                const existing = updated.get(fileId) || state;
                updated.set(fileId, {
                  ...existing,
                  status: 'uploading',
                  progress,
                });
                if (onUploadProgress) {
                  const currentStates = Array.from(updated.values())
                    .filter((s) => s.progress)
                    .map((s) => ({ file: s.file, progress: s.progress! }));
                  onUploadProgress(currentStates);
                }
                return updated;
              });
            },
          )
            .then((result) => {
              if (result.success) {
                setUploadStates((prev) => {
                  const updated = new Map(prev);
                  const existing = updated.get(fileId) || state;
                  updated.set(fileId, {
                    ...existing,
                    status: 'completed',
                    result,
                    progress: {
                      loaded: state.file.size,
                      total: state.file.size,
                      percentage: 100,
                    },
                  });
                  return updated;
                });
                results.push(result);
              } else {
                setUploadStates((prev) => {
                  const updated = new Map(prev);
                  const existing = updated.get(fileId) || state;
                  updated.set(fileId, {
                    ...existing,
                    status: 'error',
                    error: result.error,
                  });
                  return updated;
                });
                console.error('Upload failed:', result.error);
                if (result.error) {
                  const message = `${state.file.name}: ${result.error}`;
                  setErrorMessages((prev) => {
                    const merged = [...prev, message];
                    return merged.slice(-5);
                  });
                  setLastError(message);
                }
              }
              decrementActiveUploads(1);
            })
            .catch((error) => {
              const errorMessage =
                error instanceof Error ? error.message : t('uploadFailed');
              setUploadStates((prev) => {
                const updated = new Map(prev);
                const existing = updated.get(fileId) || state;
                updated.set(fileId, {
                  ...existing,
                  status: 'error',
                  error: errorMessage,
                });
                return updated;
              });
              console.error('Upload error:', error);
              const message = `${state.file.name}: ${errorMessage}`;
              setErrorMessages((prev) => {
                const merged = [...prev, message];
                return merged.slice(-5);
              });
              setLastError(message);
              decrementActiveUploads(1);
            });
        },
      );

      await Promise.allSettled(uploads);

      // Call the callback with successful results
      if (results.length > 0) {
        onFilesUploaded(results);
      }

      // Clear upload states after a delay
      setTimeout(() => {
        setUploadStates(new Map());
      }, 3000);
    },
    [
      attachedFiles,
      uploadedFiles,
      maxTotalFiles,
      maxTotalSize,
      uploadStates,
      incrementActiveUploads,
      uploadFile,
      onUploadProgress,
      decrementActiveUploads,
      onFilesUploaded,
      t,
    ],
  );

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(event.target.files);
    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    focusChatInput();
  };

  // Global drag handlers to prevent the browser from opening the file
  useEffect(() => {
    const hasFiles = (event: DragEvent) =>
      Array.from(event.dataTransfer?.types || []).includes('Files');

    const handleWindowDragEnter = (event: DragEvent) => {
      if (disabled) {
        return;
      }
      if (!hasFiles(event)) {
        return;
      }
      dragCounterRef.current += 1;
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(true);
    };

    const handleWindowDragOver = (event: DragEvent) => {
      if (!hasFiles(event)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy';
      }
    };

    const handleWindowDragLeave = (event: DragEvent) => {
      if (!hasFiles(event)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
      if (dragCounterRef.current === 0) {
        setIsDragOver(false);
      }
    };

    const handleWindowDrop = (event: DragEvent) => {
      if (!hasFiles(event)) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragOver(false);
      if (!disabled && event.dataTransfer?.files?.length) {
        // Delegate to existing file processing
        // Note: convert FileList to FileList | null type expected
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        handleFileSelect(event.dataTransfer.files as any);
        focusChatInput();
      }
    };

    window.addEventListener('dragenter', handleWindowDragEnter, {
      passive: false,
    });
    window.addEventListener('dragover', handleWindowDragOver, {
      passive: false,
    });
    window.addEventListener('dragleave', handleWindowDragLeave, {
      passive: false,
    });
    window.addEventListener('drop', handleWindowDrop, { passive: false });

    return () => {
      window.removeEventListener(
        'dragenter',
        handleWindowDragEnter as EventListener,
      );
      window.removeEventListener(
        'dragover',
        handleWindowDragOver as EventListener,
      );
      window.removeEventListener(
        'dragleave',
        handleWindowDragLeave as EventListener,
      );
      window.removeEventListener('drop', handleWindowDrop as EventListener);
    };
  }, [disabled, handleFileSelect, focusChatInput]);

  const handleButtonClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!disabled && !isDragOver) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const x = event.clientX;
    const y = event.clientY;

    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    if (!disabled) {
      handleFileSelect(event.dataTransfer.files);
    }
    focusChatInput();
  };

  const hasActiveUploads = Array.from(uploadStates.values()).some(
    (state) => state.status === 'uploading',
  );

  const getButtonIcon = () => {
    if (hasActiveUploads) {
      return (
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      );
    }

    return (
      <svg
        className="h-4 w-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
        />
      </svg>
    );
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPT_TYPES}
        onChange={handleInputChange}
        className="hidden"
        disabled={disabled || hasActiveUploads}
      />
      <div className="relative">
        <button
          type="button"
          onClick={handleButtonClick}
          disabled={
            disabled ||
            hasActiveUploads ||
            attachedFiles.length + uploadedFiles.length >= maxTotalFiles ||
            attachedFiles.reduce((s, f) => s + (f.file?.size || 0), 0) +
              uploadedFiles.reduce(
                (s, r) => s + (r.uploadResult?.fileSize || 0),
                0,
              ) >=
              maxTotalSize
          }
          className={`flex h-8 w-8 items-center justify-center rounded-full transition-colors ${
            disabled ||
            hasActiveUploads ||
            attachedFiles.length + uploadedFiles.length >= maxTotalFiles ||
            attachedFiles.reduce((s, f) => s + (f.file?.size || 0), 0) +
              uploadedFiles.reduce(
                (s, r) => s + (r.uploadResult?.fileSize || 0),
                0,
              ) >=
              maxTotalSize
              ? 'cursor-not-allowed bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-gray-300'
          } ${className}`}
          title={
            hasActiveUploads
              ? t('uploading')
              : attachedFiles.length + uploadedFiles.length >= maxTotalFiles ||
                  attachedFiles.reduce((s, f) => s + (f.file?.size || 0), 0) +
                    uploadedFiles.reduce(
                      (s, r) => s + (r.uploadResult?.fileSize || 0),
                      0,
                    ) >=
                    maxTotalSize
                ? `Attachment limit reached (${maxTotalFiles} files or ${Math.floor(maxTotalSize / (1024 * 1024))}MB total)`
                : t('attachFileImagesDocumentsTextFiles')
          }
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {getButtonIcon()}
        </button>

        {/* Upload Progress Indicator */}
        {showProgress && hasActiveUploads && (
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 transform">
            <div className="rounded bg-black px-2 py-1 text-xs whitespace-nowrap text-white">
              {t('uploading')}
            </div>
          </div>
        )}
        {/* Inline error list removed intentionally to avoid duplicating messages in the input area */}
      </div>
      {/* Drag overlay for visual feedback */}
      {isDragOver && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-blue-50 backdrop-blur-sm dark:bg-blue-950/10"
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // When leaving the overlay entirely, hide it
            const rect = (
              e.currentTarget as HTMLElement
            ).getBoundingClientRect();
            const { clientX: x, clientY: y } = e;
            if (
              x < rect.left ||
              x > rect.right ||
              y < rect.top ||
              y > rect.bottom
            ) {
              setIsDragOver(false);
              dragCounterRef.current = 0;
            }
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragOver(false);
            dragCounterRef.current = 0;
            if (!disabled) {
              handleFileSelect(e.dataTransfer?.files || null);
            }
            focusChatInput();
          }}
        >
          <div className="rounded-lg border-2 border-dashed border-blue-500 bg-white p-8 shadow-lg dark:bg-gray-900">
            <div className="flex flex-col items-center gap-3 text-blue-600">
              <svg
                className="h-12 w-12"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              <p className="text-lg font-medium">{t('dropFilesHere')}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('imagesAndPDFsWillBeUploadedTo')}
              </p>
            </div>
          </div>
        </div>
      )}
      {lastError && (
        <div className="fixed top-4 right-4 z-[60] max-w-sm">
          <Alert
            variant="error"
            title={t('uploadFailed')}
            onClose={() => setLastError(null)}
          >
            {lastError}
          </Alert>
        </div>
      )}
    </>
  );
};

export default FileAttachment;
